import type { PortalMe } from "@wms/domain";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * ТЗ 1.2 раздел 14 / ADR 0015: the client cabinet has its own tokens («client_access» / «client_refresh») and its
 * own storage key, so a client session and a staff session never mix in one browser.
 */
interface PortalState {
  accessToken: string | null;
  refreshToken: string | null;
  me: PortalMe | null;
  notice: string | null;
  login: (username: string, password: string) => Promise<void>;
  loadMe: () => Promise<void>;
  refresh: () => Promise<boolean>;
  logout: (reason?: string) => void;
  can: (permission: string) => boolean;
}

export const PORTAL_BASE = import.meta.env.VITE_API_URL ?? "/api/v1";

export const usePortalStore = create<PortalState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      me: null,
      notice: null,
      async login(username, password) {
        const response = await fetch(`${PORTAL_BASE}/portal/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ username, password }),
        });
        if (!response.ok) {
          const problem = await response.json().catch(() => ({ title: "Ошибка входа" }));
          throw new Error(problem.detail ?? problem.title ?? "Ошибка входа");
        }
        const tokens = await response.json();
        set({ accessToken: tokens.access_token, refreshToken: tokens.refresh_token, notice: null });
        await get().loadMe();
      },
      async loadMe() {
        const response = await fetch(`${PORTAL_BASE}/portal/auth/me`, { headers: { Authorization: `Bearer ${get().accessToken}` } });
        if (!response.ok) {
          const problem = await response.json().catch(() => ({ title: "Не удалось загрузить профиль" }));
          throw new Error(problem.detail ?? problem.title);
        }
        set({ me: await response.json() });
      },
      async refresh() {
        const token = get().refreshToken;
        if (!token) return false;
        const response = await fetch(`${PORTAL_BASE}/portal/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: token }),
        });
        if (!response.ok) return false;
        const tokens = await response.json();
        set({ accessToken: tokens.access_token, refreshToken: tokens.refresh_token });
        return true;
      },
      logout(reason) {
        const { accessToken, refreshToken } = get();
        if (accessToken && refreshToken) {
          fetch(`${PORTAL_BASE}/portal/auth/logout`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
            body: JSON.stringify({ refresh_token: refreshToken }),
          }).catch(() => undefined);
        }
        set({ accessToken: null, refreshToken: null, me: null, notice: reason ?? null });
      },
      can(permission) {
        return get().me?.permissions.includes(permission) ?? false;
      },
    }),
    { name: "wms-portal-auth" },
  ),
);
